<template>
  <Teleport to="body">
    <Transition name="veu">
      <div v-if="aberto" class="fish-modal" @click.self="tentarFechar">
        <div
          ref="caixa"
          class="fish-modal__caixa"
          :class="`fish-modal__caixa--${largura}`"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titulo ? idDoTitulo : null"
          :aria-label="titulo ? null : rotulo"
          @keydown.esc="tentarFechar"
          @keydown.tab="prenderFoco"
        >
          <FishFrame :tom="tom" class="fish-modal__moldura">
            <template v-if="titulo" #titulo>
              <div class="fish-modal__cabecalho">
                <span :id="idDoTitulo">{{ titulo }}</span>
                <button
                  v-if="fechavel"
                  class="fish-modal__fechar alvo-invisivel"
                  type="button"
                  :aria-label="rotuloFechar"
                  @click="fechar"
                >
                  <i class="fa-times" aria-hidden="true" />
                </button>
              </div>
            </template>

            <slot />

            <template v-if="$slots.acoes" #rodape>
              <div class="fish-modal__acoes"><slot name="acoes" /></div>
            </template>
          </FishFrame>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import FishFrame from './FishFrame.vue';
import { travarRolagem, destravarRolagem } from './rolagem';

let sequencia = 0;

/** O que recebe foco. Ordem de documento — é a ordem em que o Tab anda. */
const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * 2.3 — `FishModal`: moldura, backdrop, **trinco de rolagem** e **foco preso** (UI-28, UI-30).
 *
 * ## Os dois defeitos que ele conserta
 *
 * **UI-28 — nenhum dos 9 modais travava o `body`.** Abrir um modal e rolar movia o conteúdo de trás,
 * e no telefone era pior porque a rolagem "vazava" para a página. Ver `rolagem.js` para por que
 * `overflow: hidden` sozinho não resolve.
 *
 * **UI-30 — modais sem `role="dialog"` nem `aria-modal`.** Sem isso, um leitor de tela continua
 * lendo a página de trás como se o modal não existisse, e o Tab passeia pelo conteúdo escondido.
 *
 * ## A regra do D11 sobre confirmação
 *
 * > **Ação reversível não pede confirmação.** Modal de "tem certeza?" só para o que gasta dinheiro
 * > real ou destrói item.
 *
 * Este componente existe para os casos legítimos. Se um modal novo está confirmando algo
 * reversível, o conserto é apagar o modal, não estilizá-lo.
 *
 * ## Por que `Teleport`
 *
 * O modal vai para o `<body>`. Sem isso ele herda o contexto de empilhamento do pai, e um pai com
 * `transform` (as animações de repouso do D10-3 têm) **prende `position: fixed` dentro dele** — o
 * modal ficaria preso na caixa do pai em vez de cobrir a tela. É um caso em que o CSS não avisa; só
 * fica errado.
 */
export default {
  name: 'FishModal',
  components: { FishFrame },
  props: {
    aberto: { type: Boolean, required: true },
    titulo: { type: String, default: '' },
    /** Para quando não há título visível: o leitor de tela precisa de um nome de qualquer forma. */
    rotulo: { type: String, default: 'Janela' },
    rotuloFechar: { type: String, default: 'Fechar' },
    tom: { type: String, default: 'papel' },
    fechavel: { type: Boolean, default: true },
    /**
     * A largura máxima.
     *
     * `normal` (520px) serve a confirmação e aviso, que é a maioria. `largo` (1000px) existe para o
     * que é **meia tela de lugar e meia de painel** (D16): a loja, a barraca do clã e a casa. Nesses,
     * 520px espremeria o NPC de volta ao tamanho de miniatura que o D16 está corrigindo.
     */
    largura: { type: String, default: 'normal', validator: (v) => ['normal', 'largo'].includes(v) },
  },
  emits: ['fechar'],
  data() {
    sequencia += 1;
    return { idDoTitulo: `fish-modal-titulo-${sequencia}`, focoAnterior: null };
  },
  watch: {
    aberto: {
      immediate: true,
      handler(abriu) {
        if (abriu) this.aoAbrir();
        else this.aoFechar();
      },
    },
  },
  beforeUnmount() {
    // Um modal desmontado enquanto aberto (troca de rota no meio) deixaria o `body` travado para
    // sempre. É o tipo de bug que só aparece depois, e sempre como "a página parou de rolar".
    this.aoFechar();
  },
  methods: {
    async aoAbrir() {
      this.focoAnterior = document.activeElement;
      travarRolagem();
      await this.$nextTick();

      // O foco vai para a caixa, não para o primeiro botão: mandá-lo para um botão faz o leitor de
      // tela anunciar o botão e engolir o título, e o usuário nunca fica sabendo em que janela está.
      this.$refs.caixa?.focus?.();
      const primeiro = this.$refs.caixa?.querySelector(FOCAVEIS);
      if (primeiro) primeiro.focus();
    },
    aoFechar() {
      destravarRolagem();
      // O foco volta para onde estava. Sem isso ele cai no `<body>` e o teclado recomeça do topo da
      // página — que, num jogo com barra de navegação, são muitas tabulações até voltar ao lugar.
      if (this.focoAnterior?.focus) this.focoAnterior.focus();
      this.focoAnterior = null;
    },
    fechar() {
      this.$emit('fechar');
    },
    tentarFechar() {
      if (this.fechavel) this.fechar();
    },
    /**
     * O foco preso: Tab no último volta ao primeiro, Shift+Tab no primeiro vai ao último.
     *
     * Sem isso o Tab sai do modal e passeia pela página de trás — que continua lá, só que escondida
     * atrás do véu. O usuário de teclado fica digitando em campos que não vê.
     */
    prenderFoco(evento) {
      const focaveis = [...(this.$refs.caixa?.querySelectorAll(FOCAVEIS) || [])];
      if (!focaveis.length) return;

      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];

      if (evento.shiftKey && document.activeElement === primeiro) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      }
    },
  },
};
</script>

<style scoped>
.fish-modal {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--e-4);
  /* O véu escurece; ele não borra. `backdrop-filter: blur` custa caro em telefone e o D0 não pede
   * efeito nenhum aqui — pede que o de trás saia de cena. */
  background: var(--veu);
}

.fish-modal__caixa {
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
  outline: none;
}

/* O modal largo: meia tela de lugar, meia de painel (D16). Em 520px o NPC voltaria a ser a
 * miniatura que o D16 está corrigindo. */
.fish-modal__caixa--largo {
  max-width: 1000px;
}

.fish-modal__moldura {
  animation: chegar var(--t-medio) var(--curva-quique) both;
}

/* No desktop o modal é o PERGAMINHO do Paper Pack (pedido do autor, 03/09): a folha com as abas
 * laterais enroladas, 9-slice de `pergaminho.png` (96×96, abas de 24px). No telefone fica a
 * moldura de sempre — a folha estreita não tem largura para gastar com abas. O `fill` pinta o
 * miolo com o papel do próprio pergaminho. */
@media (min-width: 600px) {
  .fish-modal__moldura {
    border-style: solid;
    border-color: transparent;
    border-width: 12px 24px;
    border-image: url('/ui/pergaminho.png') 12 24 fill / 12px 24px round;
    background: none;
    image-rendering: pixelated;
  }
}

.fish-modal__cabecalho {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--e-3);
}

.fish-modal__fechar {
  color: var(--tinta-fraca);
  font-size: var(--f-4);
  padding: var(--e-1);
  transition: color var(--t-rapido) var(--curva-suave);
}

@media (hover: hover) {
  .fish-modal__fechar:hover {
    color: var(--tinta);
  }
}

.fish-modal__acoes {
  display: flex;
  gap: var(--e-2);
  justify-content: flex-end;
  flex-wrap: wrap;
}

/* No telefone as ações viram uma coluna e a principal fica embaixo, ao alcance do polegar (D11).
 * `column-reverse` é o que põe o primeiro slot (a ação principal) na base sem inverter a ordem do
 * DOM — o leitor de tela e o Tab continuam vendo a principal primeiro. */
@media (max-width: 599px) {
  .fish-modal__acoes {
    flex-direction: column-reverse;
  }
}

.veu-enter-active,
.veu-leave-active {
  transition: opacity var(--t-medio) var(--curva-suave);
}

.veu-enter-from,
.veu-leave-to {
  opacity: 0;
}
</style>

<style>
/* SEM scoped: o `:has` mira a classe interna do FishButton (componente filho), que o escopo não
 * alcança. O motivo do botão é absoluto e pende ABAIXO dele; dentro do rodapé do modal ele pendia
 * por cima da moldura — quando há motivo, o rodapé abre o espaço que ele ocupa. */
.fish-modal__acoes:has(.fish-button__motivo) {
  padding-bottom: 1.5em;
}
</style>
